"use client";

import { useState, useRef } from "react";
import { Send, CheckCircle2, User, Mail, Phone, Calendar, Sparkles, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const ageOptions = ["16 do 18", "19 do 25", "26 do 30", "30+"];
const availabilityOptions = ["Vikendi", "Radni dani navečer", "Fleksibilno", "Projektno / povremeno"];
const skillOptions = [
  "Komunikacija i PR", "Fotografija / video", "Dizajn i kreativnost",
  "Pisanje i urednik sadržaja", "Organizacija događaja", "Programiranje / digitalno",
  "Fundraising i prijave projekata", "Rad s mladima / edukacija",
];

export function PridruziseForm() {
  const [selectedAge, setSelectedAge] = useState("");
  const [selectedAvailability, setSelectedAvailability] = useState("");
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const motivationRef = useRef<HTMLTextAreaElement>(null);

  const toggleSkill = (s: string) =>
    setSelectedSkills((p) => p.includes(s) ? p.filter((x) => x !== s) : [...p, s]);

  const chipCls = (active: boolean, color: "emerald" | "sky" | "violet") =>
    `px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${active
      ? color === "emerald" ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-sm"
        : color === "sky" ? "bg-gradient-to-r from-sky-500 to-blue-500 text-white shadow-sm"
        : "bg-gradient-to-r from-violet-500 to-purple-500 text-white shadow-sm"
      : "bg-muted text-muted-foreground hover:bg-muted/80"
    }`;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!nameRef.current?.value || !emailRef.current?.value) {
      setError("Ime i e-mail su obavezni polja.");
      return;
    }
    setSubmitting(true);
    setError("");

    try {
      const res = await fetch("/api/submissions/pridruzise", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: nameRef.current?.value,
          email: emailRef.current?.value,
          phone: phoneRef.current?.value || null,
          age: selectedAge,
          motivation: motivationRef.current?.value || null,
          skills: selectedSkills.join(", ") || null,
          availability: selectedAvailability,
        }),
      });
      if (!res.ok) throw new Error();
      setSubmitted(true);
    } catch {
      setError("Došlo je do greške. Pokušajte ponovo.");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <section className="py-20 pb-32">
        <div className="max-w-xl mx-auto px-6 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-xl shadow-emerald-500/25">
            <CheckCircle2 className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-foreground mb-3" style={{ fontFamily: "var(--font-display)" }}>
            Dobrodošao/la u Kolektiv! 🎉
          </h2>
          <p className="text-muted-foreground text-lg">
            Tvoja prijava je zaprimljena. Javit ćemo ti se u najkraćem mogućem roku!
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 pb-32">
      <div className="max-w-2xl mx-auto px-6">
        <form className="space-y-6" onSubmit={handleSubmit}>

          {/* Personal info card */}
          <div className="p-6 md:p-8 rounded-2xl bg-card border border-border space-y-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <User className="w-5 h-5 text-emerald-600" />
              Osobni podaci
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-muted-foreground mb-1.5 block">
                  Ime i prezime <span className="text-red-500">*</span>
                </Label>
                <Input ref={nameRef} placeholder="Ana Anić" className="rounded-xl bg-background" required />
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground mb-1.5 block">
                  E-mail adresa <span className="text-red-500">*</span>
                </Label>
                <Input ref={emailRef} type="email" placeholder="ana@email.com" className="rounded-xl bg-background" required />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-muted-foreground mb-1.5 block">
                Broj mobitela <span className="text-slate-400 text-xs">(opcionalno)</span>
              </Label>
              <Input ref={phoneRef} type="tel" placeholder="+385 91 234 5678" className="rounded-xl bg-background" />
            </div>
          </div>

          {/* Age */}
          <div className="p-6 md:p-8 rounded-2xl bg-card border border-border">
            <Label className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2 block">
              <Calendar className="w-5 h-5 text-emerald-600" /> Dob
            </Label>
            <div className="flex flex-wrap gap-3">
              {ageOptions.map((age) => (
                <button key={age} type="button" onClick={() => setSelectedAge(age)} className={chipCls(selectedAge === age, "emerald")}>{age}</button>
              ))}
            </div>
          </div>

          {/* Skills */}
          <div className="p-6 md:p-8 rounded-2xl bg-card border border-border">
            <Label className="text-lg font-semibold text-foreground mb-1 flex items-center gap-2 block">
              <Sparkles className="w-5 h-5 text-sky-600" /> Što znaš raditi?
            </Label>
            <p className="text-sm text-muted-foreground mb-4">Odaberi jedno ili više područja</p>
            <div className="flex flex-wrap gap-3">
              {skillOptions.map((s) => (
                <button key={s} type="button" onClick={() => toggleSkill(s)} className={chipCls(selectedSkills.includes(s), "sky")}>{s}</button>
              ))}
            </div>
          </div>

          {/* Availability */}
          <div className="p-6 md:p-8 rounded-2xl bg-card border border-border">
            <Label className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2 block">
              <Clock className="w-5 h-5 text-violet-600" /> Dostupnost
            </Label>
            <div className="flex flex-wrap gap-3">
              {availabilityOptions.map((a) => (
                <button key={a} type="button" onClick={() => setSelectedAvailability(a)} className={chipCls(selectedAvailability === a, "violet")}>{a}</button>
              ))}
            </div>
          </div>

          {/* Motivation */}
          <div className="p-6 md:p-8 rounded-2xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100">
            <Label className="text-lg font-semibold text-foreground mb-2 block">
              Zašto se želiš pridružiti?
            </Label>
            <p className="text-sm text-muted-foreground mb-4">
              Reci nam nešto o sebi i zašto te privlači rad u Kolektivu. <span className="text-slate-400">(opcionalno)</span>
            </p>
            <Textarea
              ref={motivationRef}
              placeholder="Zanima me volontiranje, ekologija, rad s mladima..."
              className="rounded-xl bg-white min-h-[120px]"
            />
          </div>

          {error && (
            <p className="text-red-500 text-sm text-center bg-red-50 border border-red-100 rounded-xl p-3">{error}</p>
          )}

          <Button
            type="submit"
            size="lg"
            disabled={submitting}
            className="w-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold py-6 text-lg border-0 shadow-lg shadow-emerald-500/25 disabled:opacity-60"
          >
            <Send className="w-5 h-5 mr-2" />
            {submitting ? "Šalje se..." : "Pošalji prijavu"}
          </Button>
        </form>
      </div>
    </section>
  );
}
